import React, { useState } from 'react';
import Calendar from '@components/common/Calendar.jsx';
import moment from 'moment';

/**
 * 페이지 프리셋 입력 모달 컴포넌트
 * @param {Object} props - 컴포넌트 속성
 * @param {string} props.title - 모달 제목
 * @param {Object} props.inputs - 모달 입력값 상태
 * @param {Function} props.setInputs - 모달 입력값 설정 함수
 * @param {Function} props.onClose - 모달 닫기 함수
 * @param {Function} props.onConfirm - 확인 버튼 클릭 시 실행 함수
 * @param {Function} props.onCheckDuplicate - 중복 확인 함수
 * @param {boolean} props.isEdit - 수정 모드 여부
 * @param {Array} props.availableBranches - 사용 가능한 지점 목록
 * @param {Array} props.selectedBranches - 선택된 지점 목록
 * @param {Function} props.onAddBranch - 지점 추가 함수
 * @param {Function} props.onRemoveBranch - 지점 제거 함수
 * @param {Array} props.themes - 디자인 테마 목록
 * @param {boolean} props.showToggle - 토글 표시 여부
 * @param {string} props.toggleLabel - 토글 라벨 텍스트
 * @param {string} props.toggleName - 토글 입력 필드 이름
 * @param {boolean} props.showSettingButton - 설정 버튼 표시 여부
 * @param {string} props.settingButtonText - 설정 버튼 텍스트
 * @param {Function} props.onSettingClick - 설정 버튼 클릭 시 실행 함수
 */
const PagePresetInputModal = ({
  title,
  inputs,
  setInputs,
  onClose,
  onConfirm,
  onCheckDuplicate,
  isEdit = false,
  availableBranches = [],
  selectedBranches = [],
  onAddBranch,
  onRemoveBranch,
  themes = [],
  showToggle = true,
  toggleLabel = "활성화 여부",
  toggleName = "isActive",
  showSettingButton = false,
  settingButtonText = '',
  onSettingClick
}) => {
  // 모달 입력값 변경 핸들러
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setInputs(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // 다중 선택을 위한 상태 추가
  const [selectedAvailableBranches, setSelectedAvailableBranches] = useState([]);
  const [selectedAddedBranches, setSelectedAddedBranches] = useState([]);

  // 달력 관련 상태
  const [showCalendar, setShowCalendar] = useState(false);
  const [calendarPosition, setCalendarPosition] = useState({
    top: 0,
    left: 0,
  });
  const [activeInput, setActiveInput] = useState(null);

  // 사용 가능한 지점 목록 (이미 선택된 지점 제외)
  const availableBranchList = availableBranches.filter(branch => 
    !selectedBranches.some(selected => selected.id === branch.id)
  );

  // 지점 체크박스 변경 핸들러
  const handleBranchCheckboxChange = (type, branchId) => {
    if (type === 'available') {
      setSelectedAvailableBranches(prev => {
        if (prev.includes(branchId)) {
          return prev.filter(id => id !== branchId);
        } else {
          return [...prev, branchId];
        }
      });
    } else {
      setSelectedAddedBranches(prev => {
        if (prev.includes(branchId)) {
          return prev.filter(id => id !== branchId);
        } else {
          return [...prev, branchId];
        }
      });
    }
  };
  
  // 지점 추가 핸들러
  const handleMultiAddBranch = () => {
    if (selectedAvailableBranches.length > 0) {
      const branchesToAdd = availableBranchList.filter(branch => 
        selectedAvailableBranches.includes(branch.id)
      );
      onAddBranch(branchesToAdd);
      setSelectedAvailableBranches([]);
    }
  };

  // 지점 제거 핸들러
  const handleMultiRemoveBranch = () => {
    if (selectedAddedBranches.length > 0) {
      onRemoveBranch(selectedAddedBranches);
      setSelectedAddedBranches([]);
    }
  };
  
  // 달력 선택 핸들러
  const handleDateClick = (dateType, event) => {
    const input = event.target;
    const rect = input.getBoundingClientRect();

    setCalendarPosition({
      top: rect.bottom + window.scrollY,
      left: rect.left,
    });

    setActiveInput(dateType);
    setShowCalendar(true);
  };

  // 날짜 선택 핸들러
  const handleDateSelect = (startDatetime, endDatetime) => {
    // 활성화된 입력 필드에 따라 시작일 또는 종료일만 업데이트
    if (activeInput === 'startDatetime') {
      // 시작일 입력 필드가 활성화된 경우
      let _startDatetime = '';
      if (moment(startDatetime).isValid()) {
        _startDatetime = moment(startDatetime).format('YYYYMMDD');
      }

      setInputs(prev => ({
        ...prev,
        startDatetime: _startDatetime
      }));
    } else if (activeInput === 'endDatetime') {
      // 종료일 입력 필드가 활성화된 경우
      let _endDatetime = '';
      // 여기서 중요: 종료일의 경우 두 번째 인수인 endDatetime을 사용해야 함
      // 하지만 단일 날짜 선택의 경우 startDatetime에 선택된 날짜가 들어있음
      const dateToUse = endDatetime || startDatetime;
      if (moment(dateToUse).isValid()) {
        _endDatetime = moment(dateToUse).format('YYYYMMDD');
      }

      setInputs(prev => ({
        ...prev,
        endDatetime: _endDatetime
      }));
    }

    setShowCalendar(false);
  };
  const handleThemeChange = (e) => {
    const { name, value } = e.target;
    setInputs(prev => ({
      ...prev,
      [name]: value
    }));

    // 테마 선택 시 프리셋명 자동 생성 (수정 모드에서는 자동 생성하지 않음)
    if (!isEdit && value) {
      const selectedTheme = themes.find(theme => theme.id === parseInt(value));
      if (selectedTheme) {
        // 선택된 지점이 있으면 첫 번째 지점명 사용, 없으면 기본값
        const branchSuffix = selectedBranches.length > 0 
          ? selectedBranches[0].name 
          : '기본';
        const defaultPresetName = `${selectedTheme.themeName}_${branchSuffix}`;
        setInputs(prev => ({
          ...prev,
          presetName: defaultPresetName
        }));
      }
    }
  };

  return (
    <div className="modal-overlay active" id={isEdit ? "edit-modal" : "create-modal"}>
      <div className="modal-container">
        <div className="modal-header">
          <h3 className="modal-title">{title}</h3>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>
        <div className="modal-body">
          <div className="modal-form-group">
            <label className="modal-label">디자인 테마 목록</label>
            <div className="modal-select-container">
              <select
                className="modal-select"
                name="themeId"
                value={inputs.themeId}
                onChange={handleThemeChange}
                >
                <option value="">디자인 테마를 선택하세요</option>
                {themes.map(theme => (
                  <option key={theme.id} value={theme.id}>
                    {theme.themeName}
                  </option>
                ))}
              </select>
              <span className="modal-select-arrow">
                <i className="ri-arrow-down-s-line"></i>
              </span>
              </div>
            </div>
          
          <div className="modal-form-group">
            <label className="modal-label">프리셋명</label>
            <div className="modal-input-with-button">
              <input 
                type="text" 
                className="modal-input" 
                name="presetName"
                value={inputs.presetName}
                onChange={handleInputChange}
                placeholder="프리셋명을 입력하세요"
              />
              <button 
                className="modal-btn modal-btn-secondary" 
                onClick={onCheckDuplicate}
              >
                중복확인
              </button>
            </div>
          </div>
          
          <div className="modal-form-group">
            <label className="modal-label">사용기간</label>
            <div className="date-range-container">
              <input
                type="text"
                className="modal-input date-input"
                id="start-date"
                name="startDatetime"
                value={inputs.startDatetime || ''}
                placeholder="시작일"
                readOnly
                onClick={(e) => handleDateClick("startDatetime", e)}
              />
              <span className="date-separator">-</span>
              <input
                type="text"
                className="modal-input date-input"
                id="end-date"
                name="endDatetime"
                value={inputs.endDatetime || ''}
                placeholder="종료일"
                readOnly
                onClick={(e) => handleDateClick("endDatetime", e)}
              />
            </div>
          </div>
          
          <div className="modal-form-group">
            <label className="modal-label">시스템 적용</label>
            <div className="permission-transfer-container">
              <div className="permission-box">
                <div className="permission-header">사용 가능 시스템</div>
                <div className="permission-list" id="available-systems">
                  {availableBranchList.map(branch => (
                    <div 
                      key={branch.id}
                      className={`permission-item ${selectedAvailableBranches.includes(branch.id) ? 'selected' : ''}`}
                    >
                      <input 
                        type="checkbox" 
                        id={`available-${branch.id}`} 
                        name="branch-select" 
                        className="permission-checkbox"
                        checked={selectedAvailableBranches.includes(branch.id)}
                        onChange={() => handleBranchCheckboxChange('available', branch.id)}
                      />
                      <label htmlFor={`available-${branch.id}`} className="permission-label">{branch.name}</label>
                    </div>
                  ))}
                  {availableBranchList.length === 0 && (
                    <div className="permission-item permission-empty">
                      <span className="permission-label">사용 가능한 지점이 없습니다.</span>
                    </div>
                  )}
                </div>
              </div>
              
              <div className="permission-controls">
                <button 
                  id="add-system-btn" 
                  className="permission-control-btn"
                  onClick={handleMultiAddBranch}
                  disabled={selectedAvailableBranches.length === 0}
                >
                  <i className="ri-arrow-right-line"></i>
                </button>
                <button 
                  id="remove-system-btn" 
                  className="permission-control-btn"
                  onClick={handleMultiRemoveBranch}
                  disabled={selectedAddedBranches.length === 0}
                >
                  <i className="ri-arrow-left-line"></i>
                </button>
              </div>
              
              <div className="permission-box">
                <div className="permission-header">선택된 시스템</div>
                <div className="permission-list" id="selected-systems">
                  {selectedBranches.map(branch => (
                    <div 
                      key={branch.id}
                      className={`permission-item ${selectedAddedBranches.includes(branch.id) ? 'selected' : ''}`}
                    >
                      <input 
                        type="checkbox" 
                        id={`selected-${branch.id}`} 
                        name="branch-select" 
                        className="permission-checkbox"
                        checked={selectedAddedBranches.includes(branch.id)}
                        onChange={() => handleBranchCheckboxChange('added', branch.id)}
                      />
                      <label htmlFor={`selected-${branch.id}`} className="permission-label">{branch.name}</label>
                    </div>
                  ))}
                  {selectedBranches.length === 0 && (
                    <div className="permission-item permission-empty">
                      <span className="permission-label">선택된 지점이 없습니다.</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          
          {showToggle && (
            <div className="modal-form-group toggle-group">
              <div className="toggle-label">{toggleLabel}</div>
              <div className="toggle-switch-container">
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    id={toggleName}
                    name={toggleName}
                    checked={inputs[toggleName]}
                    onChange={handleInputChange}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>
            </div>
          )}
          
          {showSettingButton && (
            <div className="modal-form-group">
              <button 
                className="modal-btn modal-btn-secondary modal-btn-full"
                onClick={onSettingClick}
              >
                {settingButtonText}
              </button>
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button 
            className="modal-btn modal-btn-cancel" 
            onClick={onClose}
          >
            취소
          </button>
          <button 
            className="modal-btn modal-btn-primary" 
            onClick={onConfirm}
          >
            프리셋 등록하기
          </button>
        </div>
      </div>
      
      {/* 달력 컴포넌트 */}
      {showCalendar && (
        <Calendar
          onDateSelect={handleDateSelect}
          onClose={() => setShowCalendar(false)}
          position={calendarPosition}
        />
      )}
    </div>
  );
};

export default PagePresetInputModal; 